import { DEFAULT_PHONE_REGION, PHONE_COUNTRIES } from "./phoneCountries";

const COUNTRY_BY_ISO = new Map(PHONE_COUNTRIES.map((country) => [country.iso, country]));

const COUNTRIES_BY_DIAL_LENGTH = [...PHONE_COUNTRIES].sort(
  (a, b) => b.dial.length - a.dial.length || a.name.localeCompare(b.name)
);

const NANP_NATIONAL = /^[2-9]\d{2}[2-9]\d{6}$/;
const GENERIC_E164 = /^\+[1-9]\d{6,14}$/;

/**
 * Regional indicator emoji for an ISO 3166-1 alpha-2 code.
 *
 * @param {string} iso
 * @returns {string}
 */
export function flagEmoji(iso) {
  const code = (iso || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(...[...code].map((ch) => 127397 + ch.charCodeAt(0)));
}

/**
 * @param {string} iso
 * @returns {{ iso: string, name: string, dial: string }|undefined}
 */
export function countryByIso(iso) {
  return COUNTRY_BY_ISO.get((iso || "").toUpperCase());
}

/**
 * Digits only, preserving a leading plus when present.
 *
 * @param {string} value
 * @returns {string}
 */
export function stripPhoneInput(value) {
  const raw = typeof value === "string" ? value : "";
  const hasPlus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Match the longest calling-code prefix. Shared codes prefer the given region.
 *
 * @param {string} e164
 * @param {string} [preferredIso]
 * @returns {{ iso: string, name: string, dial: string }|undefined}
 */
export function matchCountryByE164(e164, preferredIso = "") {
  const digits = (e164 || "").replace(/^\+/, "");
  if (!digits) return undefined;
  const matches = COUNTRIES_BY_DIAL_LENGTH.filter((country) => digits.startsWith(country.dial));
  if (matches.length === 0) return undefined;
  const longest = matches[0].dial.length;
  const longestMatches = matches.filter((country) => country.dial.length === longest);
  const preferred = countryByIso(preferredIso);
  if (preferred && longestMatches.some((country) => country.iso === preferred.iso)) {
    return preferred;
  }
  return longestMatches[0];
}

/**
 * Format national digits for display while typing.
 *
 * @param {string} iso
 * @param {string} digits
 * @returns {string}
 */
export function formatNationalNumber(iso, digits) {
  const national = (digits || "").replace(/\D/g, "");
  if (iso === "US" || iso === "CA") {
    const clipped = national.slice(0, 10);
    if (clipped.length <= 3) return clipped;
    if (clipped.length <= 6) return `(${clipped.slice(0, 3)}) ${clipped.slice(3)}`;
    return `(${clipped.slice(0, 3)}) ${clipped.slice(3, 6)}-${clipped.slice(6)}`;
  }
  return national;
}

/**
 * Normalize national digits for a region (strip trunk prefixes like a leading 1 in NANP).
 *
 * @param {string} iso
 * @param {string} digits
 * @returns {string}
 */
export function normalizeNationalDigits(iso, digits) {
  let national = (digits || "").replace(/\D/g, "");
  const country = countryByIso(iso);
  if (!country) return national;
  if ((iso === "US" || iso === "CA") && national.length === 11 && national.startsWith("1")) {
    national = national.slice(1);
  }
  if (national.startsWith(country.dial) && national.length > country.dial.length + 3) {
    national = national.slice(country.dial.length);
  }
  return national;
}

/**
 * @param {string} iso
 * @param {string} nationalDigits
 * @returns {string} E.164 or empty string
 */
export function toE164(iso, nationalDigits) {
  const country = countryByIso(iso) || countryByIso(DEFAULT_PHONE_REGION);
  const national = normalizeNationalDigits(country.iso, nationalDigits);
  if (!national) return "";
  return `+${country.dial}${national}`;
}

/**
 * Parse a stored phone (E.164 or legacy national) into region + national digits.
 *
 * @param {string} value
 * @param {string} [fallbackIso]
 * @returns {{ iso: string, national: string }}
 */
export function parsePhoneValue(value, fallbackIso = DEFAULT_PHONE_REGION) {
  const fallback = countryByIso(fallbackIso) || countryByIso(DEFAULT_PHONE_REGION);
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) {
    return { iso: fallback.iso, national: "" };
  }
  const stripped = stripPhoneInput(raw);
  if (stripped.startsWith("+")) {
    const country = matchCountryByE164(stripped, fallback.iso) || fallback;
    return {
      iso: country.iso,
      national: stripped.slice(1 + country.dial.length),
    };
  }
  return {
    iso: fallback.iso,
    national: normalizeNationalDigits(fallback.iso, stripped),
  };
}

/**
 * True when value is a complete E.164 number.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isValidE164(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!GENERIC_E164.test(trimmed)) return false;
  const country = matchCountryByE164(trimmed);
  if (!country) return true;
  const national = trimmed.slice(1 + country.dial.length);
  if (country.dial === "1" || country.dial.startsWith("1")) {
    if (country.dial === "1") {
      return NANP_NATIONAL.test(national);
    }
    return /^\d{7}$/.test(national);
  }
  return national.length >= 4 && national.length <= 14;
}

/**
 * Filter countries by name, ISO code, or calling code.
 *
 * @param {string} query
 * @returns {{ iso: string, name: string, dial: string }[]}
 */
export function filterPhoneCountries(query) {
  const needle = (query || "").trim().toLowerCase().replace(/^\+/, "");
  if (!needle) return PHONE_COUNTRIES;
  return PHONE_COUNTRIES.filter((country) => {
    return (
      country.name.toLowerCase().includes(needle) ||
      country.iso.toLowerCase().includes(needle) ||
      country.dial.includes(needle)
    );
  });
}
